<template>
  <div class="container mt-5">
    <h2 class="text-center mb-4">Leaderboard</h2>
    <div v-if="sortedScores.length > 0">
      <table class="table table-striped">
        <thead>
          <tr>
            <th>Rank</th>
            <th>Team Name</th>
            <th>Total Score</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(team, index) in sortedScores" :key="team.teamId">
            <td>{{ index + 1 }}</td> <!-- Rank is index + 1 -->
            <td>{{ team.teamName }}</td>
            <td>{{ team.totalScore }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else>
      <p>No Scores Available Yet</p>
    </div>
  </div>
</template>

<script>
import { ref, watch, onUnmounted } from 'vue';
import { db } from '@/firebase';
import { collection, query, where, onSnapshot, getDocs } from 'firebase/firestore';
import moment from 'moment-timezone';

export default {
  name: 'LeaderboardPage',
  props: {
    selectedLocation: String,
  },
  setup(props) {
    const scores = ref([]);
    const scoreMap = new Map();
    const unsubscribeCallbacks = [];

    const updateScores = () => {
      // Create an array from the Map and sort it
      scores.value = Array.from(scoreMap.values()).sort(
        (a, b) => b.totalScore - a.totalScore
      );
    };

    const clearSubscriptions = () => {
      while (unsubscribeCallbacks.length) {
        const unsubscribe = unsubscribeCallbacks.pop();
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      }
      scoreMap.clear();
      scores.value = [];
    };

    const fetchScores = async () => {
      clearSubscriptions();

      if (!props.selectedLocation) {
        console.error('Selected location is not defined');
        return;
      }

      const todayDate = getTodayDate();

      // Get the active game for the selected location and current date
      const gameQuery = query(
        collection(db, 'games'),
        where('location', '==', props.selectedLocation),
        where('date', '==', todayDate)
      );

      const gameSnapshot = await getDocs(gameQuery);
      if (!gameSnapshot.empty) {
        const gameId = gameSnapshot.docs[0].id;

        const teamsQuery = query(
          collection(db, 'teams'),
          where('location', '==', props.selectedLocation),
          where('gameId', '==', gameId)
        );

        const unsubscribeTeams = onSnapshot(teamsQuery, (teamsSnapshot) => {
          teamsSnapshot.forEach((teamDoc) => {
            const teamData = teamDoc.data();
            const submissionsRef = collection(db, `games/${teamData.gameId}/submissions`);
            const unsubscribeSubmissions = onSnapshot(submissionsRef, (submissionsSnapshot) => {
              let totalScore = 0;
              submissionsSnapshot.forEach((submissionDoc) => {
                const submissionData = submissionDoc.data();
                if (submissionData.teamId === teamData.teamId) {
                  totalScore += Number(submissionData.totalScore) || 0;
                }
              });

              scoreMap.set(teamData.teamId, {
                teamId: teamData.teamId,
                teamName: teamData.teamName,
                totalScore,
              });

              updateScores();
            });
            unsubscribeCallbacks.push(unsubscribeSubmissions);
          });
        });
        unsubscribeCallbacks.push(unsubscribeTeams);
      } else {
        console.log('No active game found for the current location and date.');
      }
    };

    watch(
      () => props.selectedLocation,
      () => {
        fetchScores();
      },
      { immediate: true }
    );

    onUnmounted(() => {
      clearSubscriptions();
    });

    return {
      sortedScores: scores,
    };
  },
};

function getTodayDate() {
  
  const today = moment().tz('America/New_York');


  return today.format('YYYY-MM-DD');
}
</script>

<style scoped>
.container {
  max-width: 100%;
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1rem;
}

th, td {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid #ddd;
}

th {
  background-color: #f8f9fa;
  font-weight: bold;
}

tbody tr:hover {
  background-color: #f1f1f1;
}

table.table-striped tbody tr:nth-child(even) {
  background-color: #ffcc99 !important; /* Light orange */
}

@media (max-width: 768px) {
  th, td {
    padding: 0.5rem;
  }
}
</style>
